import { useEffect, useRef, useState } from "react";

/**
 * Google Identity Services button.
 *
 * Google's GSI button needs its width set in pixels — there's no responsive
 * mode. We measure the parent container with ResizeObserver and re-render the
 * button when the size changes, so it always fits its container without
 * overflowing the page on small phones.
 *
 * IMPORTANT: callbacks (onCredential, onError) are stored in refs so that
 * parent re-renders (typing in inputs, etc.) don't re-initialize the GSI
 * widget, which would cause visible flicker every keystroke.
 */
export default function GoogleSignInButton({
    onCredential,
    onError,
    text = "continue_with",
}) {
    const ref = useRef(null);
    const [ready, setReady] = useState(false);

    // Keep latest callbacks in refs — read at call time, not bound at init.
    const onCredentialRef = useRef(onCredential);
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onCredentialRef.current = onCredential;
        onErrorRef.current = onError;
    });

    useEffect(() => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!clientId) {
            console.warn("REACT_APP_GOOGLE_CLIENT_ID not set");
            return;
        }

        let cancelled = false;
        let resizeObserver = null;
        let lastWidth = 0;

        const renderAt = (g, width) => {
            if (!ref.current) return;
            ref.current.innerHTML = "";
            g.accounts.id.renderButton(ref.current, {
                type: "standard",
                size: "large",
                theme: "outline",
                text,
                shape: "rectangular",
                logo_alignment: "left",
                // GSI accepts 200..400. Clamp to that range.
                width: Math.min(400, Math.max(200, Math.round(width))),
            });
        };

        const tryInit = () => {
            if (cancelled) return;
            const g = window.google;
            if (!g?.accounts?.id) {
                setTimeout(tryInit, 150);
                return;
            }
            try {
                g.accounts.id.initialize({
                    client_id: clientId,
                    callback: (resp) => {
                        // Always read the latest callback from the ref so we
                        // don't capture a stale handler at init time.
                        if (resp?.credential) onCredentialRef.current?.(resp.credential);
                        else onErrorRef.current?.(new Error("Sin credencial"));
                    },
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });
                // Initial render at the current container width.
                const w = ref.current?.parentElement?.clientWidth || 320;
                lastWidth = w;
                renderAt(g, w);

                // Watch parent for size changes (orientation, viewport resize).
                if (ref.current?.parentElement && "ResizeObserver" in window) {
                    resizeObserver = new ResizeObserver((entries) => {
                        const w = entries[0]?.contentRect?.width || 0;
                        // Re-render only on meaningful changes to avoid flicker.
                        if (Math.abs(w - lastWidth) > 8) {
                            lastWidth = w;
                            renderAt(g, w);
                        }
                    });
                    resizeObserver.observe(ref.current.parentElement);
                }
                setReady(true);
            } catch (e) {
                onErrorRef.current?.(e);
            }
        };
        tryInit();

        return () => {
            cancelled = true;
            resizeObserver?.disconnect();
        };
        // Intentionally only depend on `text` — callbacks are read via refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    return (
        <div className="w-full min-w-0 flex justify-center">
            <div
                ref={ref}
                data-testid="google-signin"
                className="w-full max-w-full overflow-hidden flex justify-center [&>div]:!w-full [&_iframe]:!w-full"
            />
            {!ready && (
                <div className="h-11 w-full rounded-md border border-zinc-200 bg-zinc-50 animate-pulse" />
            )}
        </div>
    );
}
